import React, { useRef, useState } from 'react';
import { validateActivityPayload, GAME_MODE_CATALOG, type PinpointPayload } from '@interactive-room';
import { emptyPinpointPayload } from '../emptyPayloads';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { createDraftId, saveActivityDraft, type ActivityDraft } from '../activityStorage';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import { generateSubjectAwareActivityDraft } from '../studioActivityGeneration';
import type { StudioLiveEditorProps } from '../types/editor.types';

export function PinpointLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: StudioLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(emptyPinpointPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const [payload, setPayload] = useState<PinpointPayload>(() => launch.payload);
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedId] = useState(draftId ?? createDraftId());
  const canvasRef = useRef<HTMLDivElement>(null);

  const meta = GAME_MODE_CATALOG.point;
  const target = payload.target ?? { x: 0.5, y: 0.5, radius: 0.08 };

  const setTarget = (patch: Partial<typeof target>) => {
    setPayload((p) => ({ ...p, target: { ...target, ...patch } }));
  };

  const handleCanvasClick = (e: React.MouseEvent) => {
    const el = canvasRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setTarget({ x, y });
  };

  const save = () => {
    try {
      validateActivityPayload('point', payload);
      setError(null);
      onSaved?.(
        saveActivityDraft({
          id: savedId,
          mode: 'point',
          title: payload.title,
          subjectTag: subjectTag.trim() || 'general',
          payload: { ...payload, target },
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    }
  };

  const handleAiBuild = async (promptText: string) => {
    setAiLoading(true);
    setError(null);
    setNotice(null);
    try {
      const { draft, notice: aiNotice } = await generateSubjectAwareActivityDraft({
        mode: 'point',
        subjectId: launchSubjectId,
        subjectTag: subjectTag.trim() || undefined,
        tuning: promptText,
        draftId: savedId,
        persist: false,
      });
      setPayload(draft.payload as PinpointPayload);
      if (draft.subjectTag) setSubjectTag(draft.subjectTag);
      if (aiNotice) setNotice(aiNotice);
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'subject_context_required'
          ? 'Selecciona una asignatura desde Herramientas para generar con IA.'
          : e instanceof Error
            ? e.message
            : 'No se pudo generar con IA',
      );
    } finally {
      setAiLoading(false);
    }
  };

  const sidebar = (
    <div className="space-y-3 text-xs">
      <label className="block">
        <span className="sala-label mb-1 block">URL imagen</span>
        <input
          className="sala-input-dark w-full text-xs"
          value={payload.imageUrl}
          onChange={(e) => setPayload((p) => ({ ...p, imageUrl: e.target.value }))}
          placeholder="https://…"
        />
      </label>
      <label className="block">
        <span className="sala-label mb-1 block">Tolerancia ({Math.round(target.radius * 100)}%)</span>
        <input
          type="range"
          min={0.03}
          max={0.2}
          step={0.01}
          value={target.radius}
          onChange={(e) => setTarget({ radius: Number(e.target.value) })}
          className="w-full accent-[var(--sala-accent-neon)]"
        />
      </label>
      <p className="text-[10px] opacity-50">Clic en la imagen para marcar la zona correcta</p>
    </div>
  );

  return (
    <StudioLiveShell
      embedded={embedded}
      layout={embedded ? 'split' : 'default'}
      error={error}
      subjectTag={subjectTag}
      onSubjectTagChange={setSubjectTag}
      onSave={save}
      aiActivityLabel="señalar"
      onAiBuild={handleAiBuild}
      aiLoading={aiLoading}
      sidebar={sidebar}
      sidebarTitle="Mapa"
    >
      {notice ? (
        <p className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-900">
          {notice}
        </p>
      ) : null}
      <header className="plugin-live-toolbar">
        <input
          className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-bold"
          value={payload.title}
          onChange={(e) => setPayload((p) => ({ ...p, title: e.target.value }))}
          placeholder="Título"
        />
        <span className="explora-chip">{meta.displayName}</span>
      </header>

      <div className="plugin-live-subtoolbar px-3 py-1.5">
        <input
          className="canvas-inline-input w-full bg-transparent text-xs"
          value={payload.prompt}
          placeholder="Pregunta…"
          onChange={(e) => setPayload((p) => ({ ...p, prompt: e.target.value }))}
        />
      </div>

      <div className="plugin-live-body relative min-h-0 flex-1 p-2">
        <div
          ref={canvasRef}
          className="pinpoint-canvas relative h-full w-full cursor-crosshair overflow-hidden rounded-lg border-2"
          style={{ borderColor: 'var(--plugin-border)' }}
          onClick={handleCanvasClick}
          role="presentation"
        >
          {payload.imageUrl ? (
            <img src={payload.imageUrl} alt="" className="h-full w-full object-contain" draggable={false} />
          ) : (
            <div className="flex h-full items-center justify-center text-sm opacity-40">Añade URL de imagen →</div>
          )}
          <div
            className="pointer-events-none absolute rounded-full border-2 border-dashed border-emerald-400 bg-emerald-400/15"
            style={{
              left: `${target.x * 100}%`,
              top: `${target.y * 100}%`,
              width: `${target.radius * 200}%`,
              height: `${target.radius * 200}%`,
              transform: 'translate(-50%, -50%)',
            }}
          />
        </div>
      </div>
    </StudioLiveShell>
  );
}
